import React, { useMemo, useState } from 'react';
import { Row, Col, Tooltip, Progress, Typography, Modal, Table, Tag } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { computeShelfStats } from './inventoryFragments';

const { Text } = Typography;

export default function ProductInventorySummary({ inventories = [] }) {
  const { t } = useTranslation();
  const [fragmentModalVisible, setFragmentModalVisible] = useState(false);

  const totalQuantity = useMemo(() =>
    inventories.reduce((sum, inv) => sum + (inv.quantity || 0), 0),
  [inventories]);

  const totalAvailable = useMemo(() =>
    inventories.reduce((sum, inv) => sum + (inv.available_quantity || 0), 0),
  [inventories]);

  const totalAllocated = useMemo(() =>
    inventories.reduce((sum, inv) => sum + (inv.allocated_quantity || 0), 0),
  [inventories]);

  const totalHeld = useMemo(() =>
    inventories.reduce((sum, inv) => sum + (inv.held_quantity || 0), 0),
  [inventories]);

  // 待上架：staging（RCV）儲位上的量，尚未進入 pickable，因此不納入主算式
  const totalPendingPutaway = useMemo(() =>
    inventories.reduce((sum, inv) => sum + (inv.pending_putaway_quantity || 0), 0),
  [inventories]);

  // 儲位統計 + 碎片偵測（抽到 inventoryFragments，與「儲位分佈」視角共用同一份判定）
  const { totalShelves, concentration, fragments } = useMemo(
    () => computeShelfStats(inventories),
    [inventories],
  );

  const concentrationColor = concentration === null ? '#8c8c8c' : concentration >= 80 ? '#52c41a' : concentration >= 50 ? '#faad14' : '#f5222d';

  const QuantityItem = ({ label, value, color }) => (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 'bold', color, lineHeight: 1 }}>
        {value.toLocaleString()}
      </div>
    </div>
  );

  const fragmentColumns = [
    {
      title: t('models.shelf.location'),
      dataIndex: 'shelf_location',
      key: 'shelf_location',
      render: text => <Tag color="blue">{text}</Tag>,
    },
    {
      title: t('models.inventory.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right',
      render: (qty, r) => (
        <span>
          <Text type="danger" strong>{qty}</Text>
          <Text type="secondary"> / {r.benchmark_quantity}</Text>
          <Text type="secondary"> ({r.percentage}%)</Text>
        </span>
      ),
    },
    {
      title: t('models.inventory_type._'),
      dataIndex: 'inventory_type',
      key: 'inventory_type',
      render: text => text || '-',
    },
    {
      title: t('models.inventory.batch'),
      dataIndex: 'batch',
      key: 'batch',
      render: text => text || '-',
    },
    {
      title: t('models.inventory.expiration_date'),
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      render: text => text || '-',
    },
  ];

  return (
    <div style={{ padding: '20px', backgroundColor: '#fafafa', borderRadius: '8px' }}>
      <Row gutter={32} align="middle">
        {/* 左側：庫存數字 */}
        <Col flex="1">
          <Row gutter={16} justify="space-around">
            <Col>
              <QuantityItem label={t('models.inventory.available_quantity')} value={totalAvailable} color="#52c41a" />
            </Col>
            <Col>
              <div style={{ fontSize: 20, color: '#d9d9d9', paddingTop: 20 }}>+</div>
            </Col>
            <Col>
              <QuantityItem label={t('models.inventory.allocated_quantity')} value={totalAllocated} color="#1890ff" />
            </Col>
            <Col>
              <div style={{ fontSize: 20, color: '#d9d9d9', paddingTop: 20 }}>+</div>
            </Col>
            <Col>
              <QuantityItem label={t('models.inventory.held_quantity')} value={totalHeld} color={totalHeld > 0 ? '#faad14' : '#8c8c8c'} />
            </Col>
            <Col>
              <div style={{ fontSize: 20, color: '#d9d9d9', paddingTop: 20 }}>=</div>
            </Col>
            <Col>
              <QuantityItem label={t('models.inventory.quantity')} value={totalQuantity} color="#262626" />
            </Col>
            {totalPendingPutaway > 0 && (
              <>
                <Col>
                  <div style={{ width: 1, height: 40, backgroundColor: '#e8e8e8', marginTop: 14 }} />
                </Col>
                <Col>
                  <QuantityItem
                    label={t('models.inventory.pending_putaway_quantity')}
                    value={totalPendingPutaway}
                    color="#fa8c16"
                  />
                </Col>
              </>
            )}
          </Row>
        </Col>

        {/* 分隔線 */}
        <Col>
          <div style={{ width: 1, height: 60, backgroundColor: '#e8e8e8' }} />
        </Col>

        {/* 右側：儲位集中度 */}
        <Col>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Tooltip title={t('shared.inventory.concentration_tooltip')}>
              <Progress
                type="circle"
                percent={concentration ?? 0}
                size={72}
                strokeColor={concentrationColor}
                format={() => <span style={{ fontSize: 18, fontWeight: 'bold', color: concentrationColor }}>{concentration === null ? '-' : `${concentration}%`}</span>}
              />
            </Tooltip>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('shared.inventory.concentration')}
              </Text>
              <div style={{ fontSize: 13 }}>
                <Text type="secondary">{t('shared.inventory.total_shelves')}：</Text>
                <Text strong>{totalShelves}</Text>
              </div>
              <div style={{ fontSize: 13 }}>
                <Text type="secondary">{t('shared.inventory.fragments')}：</Text>
                {fragments.length > 0 ? (
                  <Text
                    strong
                    style={{ color: '#f5222d', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => setFragmentModalVisible(true)}
                  >
                    {fragments.length}
                  </Text>
                ) : (
                  <Text strong style={{ color: '#52c41a' }}>0</Text>
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* 碎片儲位明細 Modal */}
      <Modal
        title={t('shared.inventory.fragments_title')}
        open={fragmentModalVisible}
        onCancel={() => setFragmentModalVisible(false)}
        footer={null}
        width={700}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          {t('shared.inventory.fragments_description')}
        </Text>
        <Table
          columns={fragmentColumns}
          dataSource={fragments}
          rowKey={(r, i) => `${r.shelf_location}-${i}`}
          pagination={false}
          size="small"
        />
      </Modal>
    </div>
  );
}
